/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Badge } from 'antd';
import { ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';

import { NodeGroup, NodeGroupFilters, OsType, PaginatedNodeGroupsResponse } from '@/client';
import { PaginatedTable } from '@/components/PaginatedTable';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Flex } from '@/primitives/Flex';
import { OsIcon } from '@/primitives/Icons';
import { Link } from '@/primitives/Link';
import { TextBadge } from '@/primitives/TextBadge';
import { Tooltip } from '@/primitives/Tooltip';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { UpdateParams } from '@/utils/params';

export type NodeGroupsTableProps = {
  nodeGroupsData: PaginatedNodeGroupsResponse;
  onParamsChange: UpdateParams<NodeGroup, Partial<NodeGroupFilters>>;
  primaryAction?: React.ReactNode;
};

export function NodeGroupsTable({ nodeGroupsData, onParamsChange, primaryAction }: NodeGroupsTableProps) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const { t } = useMimicTranslation('fleets');

  const columns: ColumnType<NodeGroup>[] = [
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      filterMode: 'menu',
      render: (name: string, record: NodeGroup) => (
        <Link
          to={`/tenants/${tenantId}/fleet-management/${record.id}`}
          style={{ fontSize: 'inherit', fontFamily: 'DM Mono' }}
        >
          {name}
        </Link>
      ),
    },
    {
      title: t('description'),
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => description || '-',
    },
    {
      title: t('os'),
      dataIndex: 'operatingSystem',
      key: 'operatingSystem',
      sorter: true,
      render: (os: OsType) => (
        <Tooltip title={t(os)}>
          <span>
            <OsIcon name={os} />
          </span>
        </Tooltip>
      ),
    },
    {
      title: t('nodeCount'),
      dataIndex: 'nodeCount',
      key: 'nodeCount',
    },
    {
      title: t('configurationStatus'),
      dataIndex: 'outOfSyncCount',
      key: 'configurationStatus',
      render: (outOfSyncCount: number) => {
        const isInSync = outOfSyncCount === 0;
        return (
          <Flex gap="xs" align="center">
            <TextBadge status={isInSync ? 'success' : 'error'}>
              <Badge status={isInSync ? 'success' : 'error'} style={{ width: '10px' }} />
              {isInSync ? t('synced') : t('outOfSync')}
            </TextBadge>
          </Flex>
        );
      },
    },
    {
      title: t('createdUtc'),
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      sorter: true,
      render: (date: string) => dayjs(date).utc().format('MM/D/YYYY HH:mm:ss'),
    },
  ];

  return (
    <PaginatedTable
      data-testid="node-groups-table"
      rowKey="id"
      data={nodeGroupsData}
      columns={columns}
      onParamsChange={onParamsChange}
      primaryAction={primaryAction}
    />
  );
}
